import { mimeTypes } from 'client/constants/editorSettings'
import { useFiles } from 'client/store'
import { DataFile } from 'client/store/files/filesReducer'
import filesSelectors from 'client/store/files/filesSelectors'
import React, { useState } from 'react'
import { SeoSettingsFields } from 'src/editor/api/website-pages-api'
import { ChevronDownIcon } from 'src/editor/shared/icons/chevron-down-icon'
import { ChevronUpIcon } from 'src/editor/shared/icons/chevron-up-icon'
import { TextInputControl } from 'src/editor/shared/settings/controls/text-input-control'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { ButtonSizeEnum, SecondaryButton, TextButton } from '../../buttons'
import { Toggle } from '../../toggle'
import { FileManager } from '../../uploader/file-manager'
import { UploadedFile } from '../../uploader/uploaded-file'

interface SeoSettingsTabProps {
  urlPath?: string
  websiteDomainName: string
  updateField: (
    field: keyof SeoSettingsFields,
  ) => (val: SeoSettingsFields[keyof SeoSettingsFields]) => void
  fields: SeoSettingsFields
}

export const SeoSettingsTab = ({
  urlPath = '',
  websiteDomainName,
  updateField,
  fields: {
    title,
    description,
    author,
    socialImage: socialImageId,
    keywords,
    index,
  },
}: SeoSettingsTabProps) => {
  const { t } = useTypedTranslation()
  const [fileManagerOpen, setFileManagerOpen] = useState(false)
  const [isPreviewActive, setIsPreviewActive] = useState(false)
  const [socialImageFile, setSocialImageFile] = useState<DataFile | null>(
    useFiles(state => filesSelectors.getFileById(state, Number(socialImageId))),
  )

  return (
    <div className="flex max-h-[618px] flex-col gap-4 overflow-y-auto">
      <span className="text-heading-4">
        {t('website_settings.sidebar.page_settings.seo')}
      </span>
      <p className="text-M text-textTertiary">
        {t('website_settings.sidebar.page_settings.seo_info')}
      </p>
      <TextInputControl
        wrapperProps={{
          className: 'flex mt-0',
          labelText: t('title'),
        }}
        value={title}
        update={updateField('title')}
      />
      <TextInputControl
        wrapperProps={{
          className: 'flex mt-0',
          labelText: t('description'),
        }}
        value={description}
        update={updateField('description')}
      />
      <TextInputControl
        wrapperProps={{
          className: 'flex mt-0',
          labelText: t('page_settings.seo.keywords.label'),
        }}
        value={keywords}
        update={updateField('keywords')}
      />
      <TextInputControl
        wrapperProps={{
          className: 'flex mt-0',
          labelText: t('page_settings.seo.author.label'),
        }}
        value={author}
        update={updateField('author')}
      />
      <TextButton
        className="justify-start"
        size={ButtonSizeEnum.Medium}
        onClick={() => setIsPreviewActive(prev => !prev)}
        title={t('core.header.pop_up.title')}
        active={isPreviewActive}
        iconBefore={isPreviewActive ? <ChevronUpIcon /> : <ChevronDownIcon />}
      >
        {t('website_settings.sidebar.page_settings.seo_search_preview_label')}
      </TextButton>
      {isPreviewActive && (
        <div className="flex flex-col rounded-md border border-border200 p-6">
          <span className="mb-1.5 text-base text-textLink">{title}</span>
          <span className="text-textTertiary text-xs">
            {`https://${websiteDomainName}/${urlPath}`}
          </span>
          {!!description && (
            <>
              <span className="my-3 h-[1px] w-full bg-divider01"></span>
              <span>{description}</span>
            </>
          )}
        </div>
      )}
      <div className="flex justify-between">
        <span className="text-heading-4">
          {t('website_settings.sidebar.page_settings.seo_image')}
        </span>
        <SecondaryButton
          className="justify-end"
          disabled={!!socialImageFile}
          onClick={() => setFileManagerOpen(true)}
        >
          {t('core.file_manager.tabs.upload')}
        </SecondaryButton>
      </div>

      {fileManagerOpen && (
        <FileManager
          closeFileManager={() => setFileManagerOpen(false)}
          mimeTypes={mimeTypes.image}
          fileType={'image'}
          onInsert={file => {
            setSocialImageFile(file)
            updateField('socialImage')(file.id)
          }}
        />
      )}
      {socialImageFile && (
        <UploadedFile
          currentFile={socialImageFile}
          cancelUpload={() => {
            setSocialImageFile(null)
            updateField('socialImage')(undefined)
          }}
          removeFile={() => {
            setSocialImageFile(null)
            updateField('socialImage')(undefined)
          }}
        />
      )}
      <Toggle
        label={'page_settings.seo.search_engines.label'}
        onChange={noIndex => updateField('index')(!noIndex)}
        checked={!index}
      />
    </div>
  )
}
